// Returns RGB triples (unitless "r g b" strings) for shades 50-900,
// derived by mixing the input hex toward white (lighter) or black (darker).
// Format matches Tailwind's `rgb(var(...) / <alpha-value>)` convention so
// opacity modifiers like `bg-primary-600/50` keep working.

const SHADE_MIX: Array<[number, 'white' | 'black' | 'none']> = [
  [85, 'white'], // 50  — very light tint
  [70, 'white'], // 100
  [55, 'white'], // 200
  [35, 'white'], // 300
  [15, 'white'], // 400
  [0, 'none'], //  500 — base
  [15, 'black'], // 600
  [30, 'black'], // 700
  [45, 'black'], // 800
  [60, 'black'], // 900
  // 950 used as the canvas page bg. Mixed deep into black so the page reads as
  // the darkest surface in the stack; `bg-white/5` panels and `bg-white/10`
  // hover states sit visibly elevated on top of it.
  [88, 'black'], // 950
]

const SHADE_KEYS = [50, 100, 200, 300, 400, 500, 600, 700, 800, 900, 950]

function hexToRgb(hex: string): { r: number; g: number; b: number } | null {
  const m = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex.trim())
  if (!m || !m[1] || !m[2] || !m[3]) return null

  return { r: parseInt(m[1], 16), g: parseInt(m[2], 16), b: parseInt(m[3], 16) }
}

function mix(c: number, pct: number, toward: 'white' | 'black'): number {
  const target = toward === 'white' ? 255 : 0

  return Math.round(c + (target - c) * (pct / 100))
}

export function deriveShades(hex: string): Record<string, string> {
  const rgb = hexToRgb(hex) ?? { r: 24, g: 144, b: 255 } // fallback to #1890ff
  const out: Record<string, string> = {}
  SHADE_MIX.forEach(([pct, toward], i) => {
    const key = SHADE_KEYS[i]

    if (toward === 'none') {
      out[`--brand-primary-${key}`] = `${rgb.r} ${rgb.g} ${rgb.b}`
    } else {
      out[`--brand-primary-${key}`] =
        `${mix(rgb.r, pct, toward)} ${mix(rgb.g, pct, toward)} ${mix(rgb.b, pct, toward)}`
    }
  })

  return out
}

// Tailwind's default `gray` (slate-ish neutral) palette — base RGB triples.
// We tint these toward the brand color at a low percentage so every
// `bg-gray-*` / `text-gray-*` / `border-gray-*` class in the app picks up a
// subtle brand undertone without losing neutrality.
const NEUTRAL_BASE: Record<number, [number, number, number]> = {
  50: [249, 250, 251],
  100: [243, 244, 246],
  200: [229, 231, 235],
  300: [209, 213, 219],
  400: [156, 163, 175],
  500: [107, 114, 128],
  600: [75, 85, 99],
  700: [55, 65, 81],
  800: [31, 41, 55],
  900: [17, 24, 39],
  950: [3, 7, 18],
}

const BRAND_TINT_PCT = 8 // % of brand color mixed into each neutral shade

export function deriveGrayShades(hex: string): Record<string, string> {
  const brand = hexToRgb(hex) ?? { r: 24, g: 144, b: 255 }
  const out: Record<string, string> = {}
  const w = BRAND_TINT_PCT / 100
  const n = 1 - w

  Object.entries(NEUTRAL_BASE).forEach(([shade, [r, g, b]]) => {
    const mr = Math.round(r * n + brand.r * w)
    const mg = Math.round(g * n + brand.g * w)
    const mb = Math.round(b * n + brand.b * w)
    out[`--brand-gray-${shade}`] = `${mr} ${mg} ${mb}`
  })

  return out
}

/** Gisify default brand palette — used before tenant branding loads (and on
 *  pre-login pages, which never mount AppLayout). */
export const DEFAULT_PRIMARY_COLOR = '#1890ff'
export const DEFAULT_SECONDARY_COLOR = '#10B981'

/** The full `--brand-*` custom-property map for a given brand palette. */
export function buildBrandVars(
  primaryColor: string,
  secondaryColor: string = DEFAULT_SECONDARY_COLOR,
): Record<string, string> {
  return {
    '--brand-primary': primaryColor,
    '--brand-primary-dark': `color-mix(in srgb, ${primaryColor} 20%, black)`,
    '--brand-secondary': secondaryColor,
    ...deriveShades(primaryColor),
    ...deriveGrayShades(primaryColor),
  }
}

/**
 * Write the `--brand-*` custom properties onto `<html>`. Tailwind's `primary`
 * and `gray` palettes resolve to `rgb(var(--brand-*))`, so without these the
 * whole app renders unstyled. Called once at bootstrap with the defaults so
 * every page (including login, which never mounts AppLayout) is styled, then
 * again by AppLayout with the tenant's colours once authenticated.
 */
export function applyBrandVars(primaryColor: string, secondaryColor?: string): void {
  const root = document.documentElement
  const vars = buildBrandVars(primaryColor, secondaryColor)
  Object.entries(vars).forEach(([k, v]) => root.style.setProperty(k, v))
}
